import { useMemo, useState, useEffect, useRef } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../utils/createScopedI18n';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import CardStats from '../../components/CardStats/CardStats';
import MonitorAddModal from './MonitorAddModal';
import { getActiveClientsWithMonitoring } from '../../services/monitoringService';
import { idMonitoringI18n } from './i18n';
import usePermissionsCrm from 'permissions/modules/crm/usePermissionsCrm';

export default function IdMonitoring() {
  const { language } = useLanguage();
  const idMonitoringLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: idMonitoringI18n });

  const mockClients = [
    {
      id: 1,
      name: 'María González',
      rut: '12.345.678-9',
      email: 'maria@example.com',
      status: 'active',
      businessLine: 'Sucursal Santiago',
    },
    {
      id: 2,
      name: 'Juan Pérez',
      rut: '9.876.543-2',
      email: 'juan@example.com',
      status: 'inactive',
      businessLine: 'Sucursal La Florida',
    },
    {
      id: 3,
      name: 'Camila Rojas',
      rut: '18.223.110-5',
      email: 'camila@example.com',
      status: 'active',
      businessLine: 'Sucursal Providencia',
    },
    {
      id: 4,
      name: 'Pedro Sánchez',
      rut: '7.552.110-4',
      email: 'pedro@example.com',
      status: 'active',
      businessLine: 'Sucursal Maipú',
    },
    {
      id: 5,
      name: 'Lorena Díaz',
      rut: '15.223.998-1',
      email: 'lorena@example.com',
      status: 'inactive',
      businessLine: 'Sucursal Ñuñoa',
    },
    {
      id: 6,
      name: 'Felipe Araya',
      rut: '20.441.221-6',
      email: 'felipe@example.com',
      status: 'active',
      businessLine: 'Sucursal Vitacura',
    },
    {
      id: 7,
      name: 'Daniel Muñoz',
      rut: '11.567.333-7',
      email: 'daniel@example.com',
      status: 'active',
      businessLine: 'Sucursal Peñalolén',
    },
    {
      id: 8,
      name: 'Valentina Silva',
      rut: '16.831.554-9',
      email: 'valentina@example.com',
      status: 'inactive',
      businessLine: 'Sucursal Las Condes',
    },
    {
      id: 9,
      name: 'Cristóbal  Reyes',
      rut: '14.774.120-2',
      email: 'cristobal@example.com',
      status: 'active',
      businessLine: 'Sucursal Huechuraba',
    },
    {
      id: 10,
      name: 'Sofía  Herrera',
      rut: '19.552.332-6',
      email: 'sofia@example.com',
      status: 'active',
      businessLine: 'Sucursal Santiago Centro',
    },
  ];

  const { DesignSystemComponents } = useOutletContext() ?? {};
  const { Card, Button, Input, Table, Badge, IconButton, Dropdown, Modal } =
    DesignSystemComponents ?? {};

  // --- Data state ---
  const [rows, setRows] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalActive, setTotalActive] = useState(0);
  const [totalInactive, setTotalInactive] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const abortControllerRef = useRef(null);

  // --- UI state ---
  const [search, setSearch] = useState('');
  const [statusFilterValue, setStatusFilterValue] = useState(null);
  const [sortKey, setSortKey] = useState('updatedAt');
  const [sortDirection, setSortDirection] = useState('desc');

  const { canAddClient, canViewClientDetail, canRemoveFromMonitoring } =
    usePermissionsCrm();

  useEffect(() => {
    idMonitoringI18n.changeLanguage(idMonitoringLanguage);
  }, [idMonitoringLanguage]);

  const statusOptions = useMemo(
    () => [
      { label: t('idMonitoring_status_all'), value: null },
      { label: t('idMonitoring_status_active_plural'), value: true },
      { label: t('idMonitoring_status_inactive_plural'), value: false },
    ],
    [t]
  );

  const statusFilter =
    statusOptions.find((option) => option.value === statusFilterValue) ||
    statusOptions[0];

  const backendSortKey = useMemo(() => {
    switch (sortKey) {
      case 'id':
        return 'identifier';
      case 'name':
        return 'name';
      case 'registeredAt':
        return 'createAt';
      case 'updatedAt':
        return 'updateAt';
      case 'status':
        return 'active';
      default:
        return 'updateAt';
    }
  }, [sortKey]);

  const backendSortDesc = useMemo(
    () => sortDirection !== 'asc',
    [sortDirection]
  );

  useEffect(() => {
    const fetchData = async () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      setLoading(true);
      try {
        const params = {
          page: page + 1,
          pageSize,
          sortBy: backendSortKey,
          desc: backendSortDesc,
        };

        if (search.trim()) {
          params.search = search.trim();
        }

        if (statusFilterValue !== null) {
          params.active = statusFilterValue;
        }

        const data = await getActiveClientsWithMonitoring(
          params,
          abortControllerRef.current.signal
        );

        const mappedRows = (data.items || []).map((item) => ({
          id: item.identifier,
          realId: item.id,
          name: item.name,
          registeredAt: item.createAt,
          updatedAt: item.updateAt,
          status: item.active ? 'active' : 'inactive',
        }));

        setRows(mappedRows);
        setTotalItems(data.total || 0);
        setTotalActive(data.totalActive || 0);
        setTotalInactive(data.totalInactive || 0);
      } catch (err) {
        if (err.name !== 'AbortError' && err.name !== 'CanceledError') {
          console.error('Error fetching monitoring clients:', err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [
    page,
    pageSize,
    search,
    statusFilterValue,
    backendSortKey,
    backendSortDesc,
  ]);

  const filtered = rows;

  const kpi = useMemo(
    () => ({
      total: totalItems,
      act: totalActive,
      inact: totalInactive,
    }),
    [totalItems, totalActive, totalInactive]
  );

  const statusColor = (status) => (status === 'active' ? 'success' : 'light');

  const getStatusLabel = (status) =>
    status === 'active'
      ? t('idMonitoring_status_active')
      : t('idMonitoring_status_inactive');

  // --- Modals: add + delete ---
  const [showAdd, setShowAdd] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [selected, setSelected] = useState(null);

  const openDelete = (row) => {
    setSelected(row);
    setShowDelete(true);
  };

  const handleDelete = () => {
    setRows((rs) => rs.filter((r) => r.id !== selected.id));
    setShowDelete(false);
    setSelected(null);
  };

  const handleAdd = (selectedClients) => {
    if (!selectedClients || !selectedClients.length) {
      setShowAdd(false);
      return;
    }

    const today = new Date().toISOString().slice(0, 10);

    setRows((rs) => [
      ...selectedClients.map((client) => ({
        id: client.rut,
        realId: client.realId ?? client.id,
        name: client.name,
        registeredAt: today,
        updatedAt: today,
        status: client.status,
      })),
      ...rs,
    ]);

    setShowAdd(false);
  };

  const onPageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(0);
  };

  const handleServerSortChange = ({ sortKey: key, direction }) => {
    setSortKey(key);
    setSortDirection(direction);
    setPage(0);
  };

  if (!Card || !Button || !Input || !Table || !Badge) return null;

  return (
    <div>
      <div className='d-flex justify-content-between align-items-start mb-4'>
        <div>
          <h2 className='font-dark fw-semibold'>{t('idMonitoring_title')}</h2>
          <h4 className='font-dark fw-normal'>{t('idMonitoring_subtitle')}</h4>
        </div>
        <Button
          variant='primary'
          iconStart='plusLg'
          size='sm'
          label={t('idMonitoring_addButton')}
          onClick={() => setShowAdd(true)}
          disabled={!canAddClient}
        />
      </div>

      <div className='row g-3 mb-4'>
        <div className='col-12 col-md-4'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('idMonitoring_kpi_total_title')}
            count={kpi.total}
            subtitle={t('idMonitoring_kpi_total_subtitle')}
            icon='person'
            fill
            loading={loading}
          />
        </div>
        <div className='col-12 col-md-4'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('idMonitoring_kpi_active_title')}
            count={kpi.act}
            subtitle={t('idMonitoring_kpi_active_subtitle')}
            icon='person'
            fill
            loading={loading}
          />
        </div>
        <div className='col-12 col-md-4'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('idMonitoring_kpi_inactive_title')}
            count={kpi.inact}
            subtitle={t('idMonitoring_kpi_inactive_subtitle')}
            icon='person'
            fill
            loading={loading}
          />
        </div>
      </div>

      <Card specialPadding='p-4'>
        <div className='row g-3 mb-3'>
          <div className='col-12 col-md-6'>
            <Input
              icon='search'
              placeholder={t('idMonitoring_search_placeholder')}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
            />
          </div>
          <div className='col-12 col-md-3'>
            <Dropdown
              options={statusOptions}
              value={statusFilter}
              onChange={(value) => {
                setStatusFilterValue(value?.value ?? null);
                setPage(0);
              }}
              placeholder={t('idMonitoring_status_all')}
            />
          </div>
        </div>
        <div className='table-container'>
          <Table
            columns={[
              {
                accessor: 'id',
                label: t('idMonitoring_table_rut'),
                width: '16%',
                sort: true,
              },
              {
                accessor: 'name',
                label: t('idMonitoring_table_name'),
                width: '32%',
                sort: true,
              },
              {
                accessor: 'registeredAt',
                label: t('idMonitoring_table_registeredAt'),
                sort: true,
                render: (row) =>
                  new Date(row.registeredAt).toLocaleDateString(
                    idMonitoringLanguage
                  ),
                width: '16%',
              },
              {
                accessor: 'updatedAt',
                label: t('idMonitoring_table_updatedAt'),
                sort: true,
                render: (row) =>
                  new Date(row.updatedAt).toLocaleDateString(
                    idMonitoringLanguage
                  ),
                width: '16%',
              },
              {
                accessor: 'status',
                label: t('idMonitoring_table_status'),
                sort: true,
                width: '12%',
                render: (row) => (
                  <Badge
                    text={getStatusLabel(row.status)}
                    color={statusColor(row.status)}
                    size='sm'
                  />
                ),
              },
              {
                label: t('idMonitoring_table_actions'),
                align: 'right',
                width: '8%',
                render: (row) => (
                  <div className='d-flex gap-2'>
                    {canViewClientDetail ? (
                      <Link
                        to={`/crm/detalles-cliente/${row.realId}`}
                        className='text-decoration-none'
                      >
                        <IconButton
                          ariaLabel={t('idMonitoring_action_viewDetails')}
                          icon='boxArrowUpRight'
                        />
                      </Link>
                    ) : (
                      <IconButton
                        ariaLabel={t('idMonitoring_action_viewDetails')}
                        icon='boxArrowUpRight'
                        disabled
                      />
                    )}

                    <IconButton
                      ariaLabel={t('idMonitoring_action_delete')}
                      icon='trash'
                      onClick={() => openDelete(row)}
                      iconStroke='var(--error-500)'
                      disabled={!canRemoveFromMonitoring}
                    />
                  </div>
                ),
              },
            ]}
            data={filtered}
            paginator
            paginationMode='server'
            page={page}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={setPage}
            onPageSizeChange={onPageSizeChange}
            loading={loading}
            serverSortKey={sortKey}
            serverSortDirection={sortDirection}
            onServerSortChange={handleServerSortChange}
            paginatorGroup={[
              { label: '10', value: 10 },
              { label: '20', value: 20 },
              { label: '50', value: 50 },
              { label: '100', value: 100 },
            ]}
          />
        </div>

        <Card specialPadding='p-3' className='mt-3'>
          <div className='d-flex align-items-start gap-2'>
            <span className='mini fw-bold'>i</span>
            <div>
              <p className='caption fw-semibold mb-1'>
                {t('idMonitoring_info_title')}
              </p>
              <p className='mini font-light mb-0'>
                {t('idMonitoring_info_body')}
              </p>
            </div>
          </div>
        </Card>

        <MonitorAddModal
          Modal={Modal}
          Input={Input}
          Dropdown={Dropdown}
          show={showAdd}
          onClose={() => setShowAdd(false)}
          onSave={handleAdd}
          clients={mockClients}
        />

        <ConfirmModal
          Modal={Modal}
          show={showDelete}
          title={t('idMonitoring_delete_confirm_title')}
          message={
            selected
              ? t('idMonitoring_delete_confirm_message', {
                  name: selected.name,
                })
              : t('idMonitoring_delete_confirm_fallback_message')
          }
          confirmLabel={t('idMonitoring_delete_confirm_button')}
          cancelLabel={t('idMonitoring_modal_cancel_button')}
          confirmVariant='danger'
          onConfirm={handleDelete}
          onClose={() => setShowDelete(false)}
        />
      </Card>
    </div>
  );
}
